import React, {useMemo} from "react";
import {Card, Space, Button, Radio, Collapse, Alert, Typography, Tag} from "antd";
import {LockOutlined} from "@ant-design/icons";

const {Text, Paragraph} = Typography;

const e = (key, label) => ({key, label});

export const BASIC_GROUPS = [
    {
        key: 'assets', label: 'Assets',
        description: 'Asset records, data versions, downloads, comments, meta exports.',
        entities: [
            e('asset', 'Asset'),
            e('asset_data_version', 'Asset Data Version'),
            e('download', 'Download'),
            e('meta_download', 'Meta Download'),
            e('meta_struct', 'Meta Struct'),
            e('comment', 'Comment'),
            e('published_asset', 'Published Asset'),
        ],
    },
    {
        key: 'tags', label: 'Tags',
        description: 'Tags, taxonomies, taggings, auto-tags, creator tags.',
        entities: [
            e('tag', 'Tag'),
            e('tagging', 'Tagging'),
            e('taxonomy', 'Taxonomy'),
            e('taxonomy_tag', 'Taxonomy Tag'),
            e('creator_tag', 'Creator Tag'),
            e('auto_tag', 'Auto Tag'),
        ],
    },
    {
        key: 'collections', label: 'Collections',
        description: 'Collection asset groups, projects, collection shares.',
        entities: [
            e('collection', 'Collection'),
            e('project', 'Project'),
            e('collection_share', 'Collection Share'),
        ],
    },
    {
        key: 'lightboxes', label: 'Lightboxes',
        description: 'Lightboxes, lightbox invites and memberships, Lightroom connections.',
        entities: [
            e('lightbox', 'Lightbox'),
            e('asset_group_invite', 'Asset Group Invite'),
            e('asset_group_membership', 'Asset Group Membership'),
            e('asset_group_lightroom_connection', 'Lightroom Connection'),
        ],
    },
    {
        key: 'storage_folders', label: 'Storage Folders',
        description: 'Storage folder hierarchy and folder-scoped assets.',
        entities: [
            e('storage_folder', 'Storage Folder'),
        ],
    },
    {
        key: 'uploads', label: 'Uploads',
        description: 'File uploads, bulk uploads, ingestions, contributions.',
        entities: [
            e('upload', 'Upload'),
            e('bulk_upload', 'Bulk Upload'),
            e('ingestion', 'Ingestion'),
            e('contribution', 'Contribution'),
        ],
    },
    {
        key: 'bulk_jobs', label: 'Bulk Jobs',
        description: 'Run bulk operations (rename, retag, edit, etc.) across many assets at once.',
        entities: [
            e('bulk_job', 'Bulk Job'),
            e('background_activity', 'Background Activity'),
        ],
    },
];

export const ADVANCED_GROUPS = [
    {
        key: 'users', label: 'Users & Memberships',
        description: 'User accounts, organization memberships, invites.',
        entities: [
            e('membership', 'Membership'),
            e('membership_request', 'Membership Request'),
            e('invite', 'Invite'),
            e('profile', 'Profile'),
        ],
    },
    {
        key: 'user_groups', label: 'User Groups',
        description: 'User group definitions and group memberships.',
        entities: [
            e('user_group', 'User Group'),
        ],
    },
    {
        key: 'permissions', label: 'Permissions',
        description: 'Asset-group permission grants.',
        entities: [
            e('permission', 'Permission'),
        ],
    },
    {
        key: 'webhooks', label: 'Webhooks',
        description: 'Outgoing webhook endpoints and delivery logs.',
        entities: [
            e('webhook', 'Webhook'),
        ],
    },
    {
        key: 'workflows', label: 'Workflows',
        description: 'Workflow definitions and steps for review/approval flows.',
        entities: [
            e('workflow', 'Workflow'),
            e('workflow_step', 'Workflow Step'),
        ],
    },
    {
        key: 'custom_meta_fields', label: 'Custom Meta Fields',
        description: 'Custom metadata field definitions and meta imports.',
        entities: [
            e('custom_meta_field', 'Custom Meta Field'),
            e('custom_meta_group', 'Custom Meta Group'),
            e('entity_type', 'Entity Type'),
            e('metadata_entity_type', 'Metadata Entity Type'),
            e('meta_import', 'Meta Import'),
            e('asset_spreadsheet_import', 'Asset Spreadsheet Import'),
            e('metadata_entity_import', 'Metadata Entity Import'),
        ],
    },
    {
        key: 'share_links', label: 'Share Links',
        description: 'Public/embargoed share links and shares.',
        entities: [
            e('share_link', 'Share Link'),
            e('share', 'Share'),
        ],
    },
    {
        key: 'access_requests', label: 'Access Requests & Grants',
        description: 'Access requests and granted-asset access flows.',
        entities: [
            e('access_request', 'Access Request'),
            e('access_grant', 'Access Grant'),
        ],
    },
    {
        key: 'rights_packages', label: 'Rights Packages',
        description: 'Rights/licensing packages assigned to assets.',
        entities: [
            e('rights_package', 'Rights Package'),
        ],
    },
    {
        key: 'reports', label: 'Reports',
        description: 'Reporting endpoints, tag/taxonomy imports, notifications.',
        entities: [
            e('tag_import', 'Tag Import'),
            e('taxonomy_import', 'Taxonomy Import'),
            e('notification', 'Notification'),
        ],
    },
    {
        key: 'organization_settings', label: 'Organization Settings',
        description: 'Org settings — branding, links, presets, filter groups, billing.',
        entities: [
            e('organization', 'Organization'),
            e('crop_preset', 'Crop Preset'),
            e('rename_preset', 'Rename Preset'),
            e('filter_group', 'Filter Group'),
            e('link', 'Link'),
            e('sitewide_announcement', 'Sitewide Announcement'),
            e('tag_suggester', 'Tag Suggester'),
            e('invoice', 'Invoice'),
        ],
    },
];

export const ALL_GROUPS = [...BASIC_GROUPS, ...ADVANCED_GROUPS];
const ALL_ENTITIES = ALL_GROUPS.flatMap(g => g.entities.map(ent => ({...ent, group: g.key})));

const LEVELS = ['none', 'read', 'write'];

export const scopeListToMap = (list) => {
    const safe = Array.isArray(list) ? list : [];
    const map = {};
    ALL_ENTITIES.forEach(ent => { map[ent.key] = 'none'; });

    safe.forEach(s => {
        const [key, level] = s.split(':');
        if (!LEVELS.includes(level)) return;

        if (map[key] !== undefined) {
            map[key] = level;
        } else {
            // Group-level scope: expand to all entities in that group
            const group = ALL_GROUPS.find(g => g.key === key);
            if (group) {
                group.entities.forEach(ent => { map[ent.key] = level; });
            }
        }
    });
    return map;
};

export const mapToScopeList = (map) => {
    return ALL_ENTITIES
        .map(ent => map[ent.key] === 'none' ? null : `${ent.key}:${map[ent.key]}`)
        .filter(Boolean);
};

const levelColor = (level) => {
    if (level === 'write') return 'blue';
    if (level === 'read') return 'green';
    return 'default';
};

export const groupSummary = (group, map) => {
    const counts = {none: 0, read: 0, write: 0};
    group.entities.forEach(ent => { counts[map[ent.key]] = (counts[map[ent.key]] || 0) + 1; });
    if (counts.write === group.entities.length) return {label: 'Write', color: 'blue'};
    if (counts.read === group.entities.length) return {label: 'Read', color: 'green'};
    if (counts.none === group.entities.length) return {label: 'None', color: 'default'};
    return {label: 'Mixed', color: 'orange'};
};

const EntityRow = ({entity, value, onChange, disabled}) => (
    <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 0'}}>
        <span>{entity.label} <Text type="secondary" style={{fontSize: 11}}>({entity.key})</Text></span>
        <Radio.Group
            size="small"
            value={value}
            disabled={disabled}
            onChange={ev => onChange(ev.target.value)}
            optionType="button"
            buttonStyle="solid"
        >
            <Radio.Button value="none">None</Radio.Button>
            <Radio.Button value="read">Read</Radio.Button>
            <Radio.Button value="write">Write</Radio.Button>
        </Radio.Group>
    </div>
);

const groupHeader = (group, map) => {
    const summary = groupSummary(group, map);
    return (
        <span style={{display: 'flex', alignItems: 'center', gap: 8}}>
            {group.label}
            <Tag color={summary.color}>{summary.label}</Tag>
            <Text type="secondary" style={{fontSize: 11}}>{group.entities.length} entities</Text>
        </span>
    );
};

export const overallLevel = (groups, map) => {
    const counts = {none: 0, read: 0, write: 0};
    let total = 0;
    groups.forEach(g => {
        g.entities.forEach(ent => {
            total++;
            const level = map[ent.key] || 'none';
            if (counts[level] !== undefined) counts[level]++;
        });
    });
    if (total === 0) return null;
    if (counts.write === total) return 'write';
    if (counts.read === total)  return 'read';
    if (counts.none === total)  return 'none';
    return null;
};

const LEVEL_LABELS = [['none', 'None'], ['read', 'Read'], ['write', 'Write']];

const BulkLevelButtons = ({active, disabled, prefix, labelPrefix, labelSeparator = ': ', onSet, onClickContainer}) => (
    <Space style={{marginBottom: 8}} wrap onClick={onClickContainer}>
        {prefix}
        {LEVEL_LABELS.map(([level, label]) => (
            <Button
                key={level}
                size="small"
                disabled={disabled}
                type={active === level ? 'primary' : 'default'}
                onClick={() => onSet(level)}
            >
                {labelPrefix ? `${labelPrefix}${labelSeparator}${label}` : label}
            </Button>
        ))}
    </Space>
);

const groupBody = ({group, map, setEntity, setGroup, disabled}) => (
    <>
        <Paragraph type="secondary" style={{marginBottom: 8}}>{group.description}</Paragraph>

        <BulkLevelButtons
            active={overallLevel([group], map)}
            disabled={disabled}
            onSet={(level) => setGroup(group, level)}
            onClickContainer={(e) => e.stopPropagation()}
            prefix={<Text type="secondary" style={{fontSize: 12}}>Set all in {group.label.toLowerCase()}:</Text>}
        />

        {group.entities.map(ent => (
            <EntityRow
                key={ent.key}
                entity={ent}
                value={map[ent.key]}
                onChange={(level) => setEntity(ent.key, level)}
                disabled={disabled}
            />
        ))}
    </>
);

const buildItems = ({groups, map, setEntity, setGroup, disabled}) =>
    groups.map(g => ({
        key: g.key,
        label: groupHeader(g, map),
        children: groupBody({group: g, map, setEntity, setGroup, disabled}),
    }));

const ScopePicker = ({value = [], onChange, isAdmin = false}) => {
    const map = useMemo(() => scopeListToMap(value), [value]);

    const setMap = (next) => onChange(mapToScopeList(next));

    const setEntity = (key, level) => setMap({...map, [key]: level});

    const setGroup = (group, level) => {
        const next = {...map};
        group.entities.forEach(ent => { next[ent.key] = level; });
        setMap(next);
    };

    const setAll = (level, groups = ALL_GROUPS) => {
        const next = {...map};
        groups.forEach(g => {
            if (!isAdmin && ADVANCED_GROUPS.includes(g)) return;
            g.entities.forEach(ent => { next[ent.key] = level; });
        });
        setMap(next);
    };

    const advancedGranted = ADVANCED_GROUPS.some(g => g.entities.some(ent => map[ent.key] !== 'none'));
    const overallActive = overallLevel(ALL_GROUPS, map);
    const basicActive   = overallLevel(BASIC_GROUPS, map);
    const advancedActive = overallLevel(ADVANCED_GROUPS, map);

    return (
        <>
            <BulkLevelButtons
                active={overallActive}
                onSet={(level) => setAll(level)}
                prefix={<Text type="secondary">Bulk:</Text>}
                labelPrefix="All"
                labelSeparator=" "
            />

            <Card size="small" title="Basic" style={{marginBottom: 12}}>
                <BulkLevelButtons
                    active={basicActive}
                    onSet={(level) => setAll(level, BASIC_GROUPS)}
                    labelPrefix="Basic"
                />
                <Collapse size="small" items={buildItems({groups: BASIC_GROUPS, map, setEntity, setGroup, disabled: false})}/>
            </Card>

            <Collapse
                size="small"
                defaultActiveKey={advancedGranted ? ['advanced'] : []}
                items={[{
                    key: 'advanced',
                    label: <span><LockOutlined/> Advanced (admin)</span>,
                    children: (
                        <>
                            {!isAdmin && (
                                <Alert
                                    type="info"
                                    showIcon
                                    style={{marginBottom: 8}}
                                    message="Advanced scopes require an admin role to grant."
                                />
                            )}
                            <BulkLevelButtons
                                active={advancedActive}
                                disabled={!isAdmin}
                                onSet={(level) => setAll(level, ADVANCED_GROUPS)}
                                labelPrefix="Advanced"
                            />
                            <Collapse size="small" items={buildItems({groups: ADVANCED_GROUPS, map, setEntity, setGroup, disabled: !isAdmin})}/>
                        </>
                    ),
                }]}
            />
        </>
    );
};

export default ScopePicker;
